/**
 * externalLinks — site-wide "external links never leave the site" guarantee.
 *
 * Craig, 2026-09-02: "make sure that all of the links always open in a new
 * tab because I do not want links that take people off my site. … like ever."
 *
 * The guarantee is STRUCTURAL, not per-render-site: a single capture-phase
 * click listener at the document root retargets any anchor whose href
 * resolves to an http(s) URL on a different origin, the moment it is
 * clicked and before the browser processes the navigation. That covers
 * every anchor that exists today and every one that ships later — CMS
 * bodyHtml injected via dangerouslySetInnerHTML, hand-written marketing
 * anchors, footer socials — without each site needing to remember
 * target="_blank".
 *
 * What it deliberately does NOT touch:
 *   - Same-origin and relative links (in-site navigation must stay in-tab).
 *   - Non-http(s) schemes (mailto:, tel: — native behaviour is correct).
 *   - Anchors that explicitly declare target="_self" — the author's opt-out
 *     for a flow that genuinely must navigate in place.
 *
 * Mechanism: we SET target/rel on the element in the capture phase rather
 * than preventDefault + window.open — the browser then handles the click
 * natively (no popup-blocker heuristics, modifier clicks keep their own
 * semantics, middle-click was already a new tab).
 */

/** Retarget `anchor` to open in a new tab if its href is an external
 *  http(s) URL. Returns true when the anchor was (re)targeted. Pure DOM,
 *  unit-testable without event plumbing. */
export function retargetIfExternal(
  anchor: HTMLAnchorElement,
  currentOrigin: string,
): boolean {
  const href = anchor.getAttribute('href')
  if (!href) return false
  // Author opt-out: an explicit same-tab declaration is respected.
  if (anchor.getAttribute('target') === '_self') return false

  let url: URL
  try {
    url = new URL(href, currentOrigin)
  } catch {
    return false
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
  if (url.origin === currentOrigin) return false

  anchor.setAttribute('target', '_blank')
  // Merge, don't clobber, any rel the author already set.
  const rel = new Set(
    (anchor.getAttribute('rel') ?? '').split(/\s+/).filter(Boolean),
  )
  rel.add('noopener')
  rel.add('noreferrer')
  anchor.setAttribute('rel', Array.from(rel).join(' '))
  return true
}

/** Install the document-level capture listener. Returns an uninstaller so
 *  React StrictMode double-mounts stay clean. */
export function installExternalLinkInterceptor(
  doc: Document = document,
): () => void {
  const onClickCapture = (e: Event) => {
    const target = e.target as Element | null
    const anchor = target?.closest?.('a[href]')
    if (anchor instanceof HTMLAnchorElement) {
      retargetIfExternal(anchor, doc.defaultView?.location.origin ?? '')
    }
  }
  doc.addEventListener('click', onClickCapture, true)
  return () => doc.removeEventListener('click', onClickCapture, true)
}
